// src/pages/Dashboard/index.js — Identity Overview (mEDGE Light & Dark Theme)
import React, { useState, useEffect, useContext, useRef, useCallback, Suspense } from "react";
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  Skeleton,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

import "../../styles/global.css";
import { ColorModeContext } from "../../index";
import ReusableHeader from "../../components/Header/ReusableHeader";

import {
  LIGHT_TOKENS,
  DARK_TOKENS,
  DASHBOARD_READ_PERMISSION,
} from "./dashboardConstants";
import {
  fetchOverview,
  fetchSignins,
  fetchMfa,
  fetchInventory,
  fetchActivity,
} from "./dashboardApi";

import KpiCardsSection from "./components/KpiCardsSection";
import NeedsAttentionCard from "./components/NeedsAttentionCard";
import IntersectionSection from "./components/IntersectionSection";

// Lazy-load heavier chart & table sections to minimize initial bundle impact
// const SignInActivitySection = React.lazy(() => import("./components/SignInActivitySection"));
const ProtocolAndMethodsSection = React.lazy(() => import("./components/ProtocolAndMethodsSection"));
const MfaOutcomesSection = React.lazy(() => import("./components/MfaOutcomesSection"));
const InventoryFleetSection = React.lazy(() => import("./components/InventoryFleetSection"));
const ActivityTabsSection = React.lazy(() => import("./components/ActivityTabsSection"));

// ─── Injected CSS (mEDGE Tokens: Flat, Zero-Shift, Glass Highlights) ──────────
const getStyles = (T) => `
  .dl-root { font-family: ${T.fontBody}; }
  .dl-page {
    min-height: 100vh;
    position: relative;
    border-radius: 12px;
  }
  .dl-inner { position: relative; z-index: 1; }

  .dl-card {
    background: ${T.bgCard};
    border: 1px solid ${T.border};
    border-radius: 12px;
    box-shadow: ${T.shadow};
    transition: background .2s ease;
    overflow: hidden;
  }
  .dl-card:hover {
    background: ${T.bgCardHover};
  }

  .dl-eyebrow {
    font-family: ${T.fontHeading};
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .12em;
    text-transform: uppercase;
    color: ${T.textMuted};
    margin-bottom: 3px;
  }

  .dl-live-dot {
    width: 7px; height: 7px;
    border-radius: 50%;
    background: ${T.accent2};
    box-shadow: 0 0 0 3px ${T.accent2}2E;
    animation: dl-pulse 2s infinite;
    display: inline-block;
  }
  @keyframes dl-pulse {
    0%,100% { box-shadow: 0 0 0 3px ${T.accent2}2E; }
    50%     { box-shadow: 0 0 0 6px ${T.accent2}0F; }
  }

  .dl-track {
    background: ${T.bgMuted};
    border-radius: 4px;
    height: 6px;
    overflow: hidden;
  }
  .dl-fill {
    height: 100%;
    border-radius: 4px;
    transition: width 0.8s cubic-bezier(.4,0,.2,1);
  }

  @keyframes dl-fade {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .dl-f  { animation: dl-fade .35s ease both; }
  .dl-f1 { animation-delay: .05s; }
  .dl-f2 { animation-delay: .10s; }
  .dl-f3 { animation-delay: .15s; }
  .dl-f4 { animation-delay: .20s; }
  .dl-f5 { animation-delay: .25s; }
  .dl-f6 { animation-delay: .30s; }

  .recharts-cartesian-grid-horizontal line,
  .recharts-cartesian-grid-vertical   line {
    stroke: ${T.gridLine} !important;
  }
  .recharts-text { fill: ${T.textMuted} !important; font-size: 11px !important; font-family: ${T.fontBody} !important; }
  .recharts-tooltip-wrapper { outline: none !important; }
  .recharts-legend-item-text { color: ${T.textSecondary} !important; font-size: 11px !important; font-family: ${T.fontBody} !important; }
`;

const SectionSkeleton = ({ height = 340 }) => (
  <div className="dl-card" style={{ height, minHeight: height, padding: "20px 22px" }}>
    <Box display="flex" justifyContent="space-between" mb={2}>
      <Skeleton variant="text" width={180} height={32} />
      <Skeleton variant="rounded" width={120} height={32} />
    </Box>
    <Skeleton variant="rectangular" width="100%" height={height - 90} sx={{ borderRadius: "8px" }} />
  </div>
);

const LS_RANGE_KEY = "dashboard_range";

/**
 * Checks if current logged-in user is an organization owner or has read permission for Dashboard
 */
const checkUserPermission = () => {
  try {
    const raw = typeof window !== 'undefined' ? (localStorage.getItem("user") || sessionStorage.getItem("user")) : null;
    if (!raw) return false;
    const user = JSON.parse(raw);

    // Organization owner bypass (AdminUser collection)
    if (user?.superadmin || user?.role === "SuperAdmin" || (user?.nameOfOrg && !user?.roleId)) {
      return true;
    }

    // Role-based exact segment check
    if (Array.isArray(user?.permissions)) {
      return (
        user.permissions.includes(DASHBOARD_READ_PERMISSION) ||
        user.permissions.some(
          (p) => typeof p === "string" && (p === DASHBOARD_READ_PERMISSION || p === "dashboard")
        )
      );
    }
    return false;
  } catch {
    return false;
  }
};

const Dashboard = () => {
  const { mode } = useContext(ColorModeContext || { mode: "light" });
  const T = mode === "dark" ? DARK_TOKENS : LIGHT_TOKENS;

  // Permission check state
  const [hasPermission] = useState(checkUserPermission);

  // Range selector (remembered in localStorage)
  const [range, setRange] = useState(() => {
    try {
      return localStorage.getItem(LS_RANGE_KEY) || "7d";
    } catch {
      return "7d";
    }
  });

  // Timestamps & Refresh state
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Partial Data / Degraded status across all endpoints
  const [degradedStatus, setDegradedStatus] = useState({
    overview: false,
    signins: false,
    mfa: false,
    inventory: false,
    activity: false,
  });

  // Overview Data (Row 1 KPIs & Row 2 Needs Attention)
  const [overviewData, setOverviewData] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState(null);

  // Sign-in Telemetry Data (Row 2 right & Row 3 top apps)
  const [signinsData, setSigninsData] = useState(null);
  const [signinsLoading, setSigninsLoading] = useState(false);
  const [signinsError, setSigninsError] = useState(null);

  // MFA Telemetry Data (Row 3 method mix & Row 4 outcomes/latency)
  const [mfaData, setMfaData] = useState(null);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState(null);

  // Inventory & Fleet Data (Row 5)
  const [inventoryData, setInventoryData] = useState(null);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryError, setInventoryError] = useState(null);

  // Activity & Events Data (Row 6)
  const [activityData, setActivityData] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState(null);

  const isAbortError = (err) => {
    return (
      err?.name === "CanceledError" ||
      err?.name === "AbortError" ||
      err?.code === "ERR_CANCELED" ||
      err?.message === "canceled"
    );
  };

  // Track which lower sections have been revealed by IntersectionObserver
  const [sectionsVisible, setSectionsVisible] = useState({
    signins: false,
    mfa: false,
    inventory: false,
    activity: false,
  });

  const sectionsVisibleRef = useRef(sectionsVisible);
  useEffect(() => {
    sectionsVisibleRef.current = sectionsVisible;
  }, [sectionsVisible]);

  const abortControllerRef = useRef(null);

  // Inject styles for active theme tokens
  useEffect(() => {
    let el = document.getElementById("dl-styles");
    if (!el) {
      el = document.createElement("style");
      el.id = "dl-styles";
      document.head.appendChild(el);
    }
    el.innerHTML = getStyles(T);
  }, [T]);

  // Handle range change
  const handleRangeChange = (newRange) => {
    if (!newRange || newRange === range) return;
    setRange(newRange);
    try {
      localStorage.setItem(LS_RANGE_KEY, newRange);
    } catch {
      // ignore
    }
  };

  // ─── Fetch Overview (Row 1 & Row 2 Attention) ──────────────────────────────
  const loadOverview = useCallback(
    async (r, signal) => {
      setOverviewLoading(true);
      setOverviewError(null);
      try {
        const res = await fetchOverview(r, signal);
        if (res && res.data) {
          setOverviewData(res.data);
          setDegradedStatus((prev) => ({ ...prev, overview: !!res.degraded }));
          setLastRefreshed(new Date());
        }
      } catch (err) {
        if (!isAbortError(err)) {
          setOverviewError(err?.message || "Failed to load overview");
        }
      } finally {
        setOverviewLoading(false);
      }
    },
    []
  );

  // ─── Fetch Signins (Row 2 & Row 3) ─────────────────────────────────────────
  const loadSignins = useCallback(
    async (r, signal) => {
      setSigninsLoading(true);
      setSigninsError(null);
      try {
        const res = await fetchSignins(r, signal);
        if (res && res.data) {
          setSigninsData(res.data);
          setDegradedStatus((prev) => ({ ...prev, signins: !!res.degraded }));
        }
      } catch (err) {
        if (!isAbortError(err)) {
          setSigninsError(err?.message || "Failed to load sign-in activity");
        }
      } finally {
        setSigninsLoading(false);
      }
    },
    []
  );

  // ─── Fetch MFA (Row 3 & Row 4) ─────────────────────────────────────────────
  const loadMfa = useCallback(
    async (r, signal) => {
      setMfaLoading(true);
      setMfaError(null);
      try {
        const res = await fetchMfa(r, signal);
        if (res && res.data) {
          setMfaData(res.data);
          setDegradedStatus((prev) => ({ ...prev, mfa: !!res.degraded }));
        }
      } catch (err) {
        if (!isAbortError(err)) {
          setMfaError(err?.message || "Failed to load MFA data");
        }
      } finally {
        setMfaLoading(false);
      }
    },
    []
  );

  // ─── Fetch Inventory (Row 5) ───────────────────────────────────────────────
  const loadInventory = useCallback(
    async (signal) => {
      setInventoryLoading(true);
      setInventoryError(null);
      try {
        const res = await fetchInventory(signal);
        if (res && res.data) {
          setInventoryData(res.data);
          setDegradedStatus((prev) => ({ ...prev, inventory: !!res.degraded }));
        }
      } catch (err) {
        if (!isAbortError(err)) {
          setInventoryError(err?.message || "Failed to load inventory");
        }
      } finally {
        setInventoryLoading(false);
      }
    },
    []
  );

  // ─── Fetch Activity (Row 6) ────────────────────────────────────────────────
  const loadActivity = useCallback(
    async (signal) => {
      setActivityLoading(true);
      setActivityError(null);
      try {
        const res = await fetchActivity(10, signal);
        if (res && res.data) {
          setActivityData(res.data);
          setDegradedStatus((prev) => ({ ...prev, activity: !!res.degraded }));
        }
      } catch (err) {
        if (!isAbortError(err)) {
          setActivityError(err?.message || "Failed to load activity");
        }
      } finally {
        setActivityLoading(false);
      }
    },
    []
  );

  // Initial load and range-change reload
  useEffect(() => {
    if (!hasPermission) return;

    // Abort any ongoing queries when range changes or unmounts
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // Always fetch top overview and signins (Row 1 & Row 2 above the fold)
    loadOverview(range, controller.signal);
    loadSignins(range, controller.signal);

    // Refetch lower sections that have already been made visible
    if (sectionsVisibleRef.current.mfa) loadMfa(range, controller.signal);
    if (sectionsVisibleRef.current.inventory) loadInventory(controller.signal);
    if (sectionsVisibleRef.current.activity) loadActivity(controller.signal);

    return () => {
      controller.abort();
    };
  }, [range, hasPermission, loadOverview, loadSignins, loadMfa, loadInventory, loadActivity]);

  // ─── Auto-Refresh Every 60s (Tab Visibility Aware) ─────────────────────────
  useEffect(() => {
    if (!hasPermission) return;

    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        const controller = new AbortController();
        loadOverview(range, controller.signal);
        loadSignins(range, controller.signal);
        if (sectionsVisibleRef.current.mfa) loadMfa(range, controller.signal);
        if (sectionsVisibleRef.current.inventory) loadInventory(controller.signal);
        if (sectionsVisibleRef.current.activity) loadActivity(controller.signal);
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [range, hasPermission, loadOverview, loadSignins, loadMfa, loadInventory, loadActivity]);

  // ─── Deep-link / Hash Listener for Failed Sign-ins & Activity ──────────────
  useEffect(() => {
    const handleNavigate = (e) => {
      const { sectionId } = e.detail || {};
      setSectionsVisible((prev) => ({ ...prev, activity: true }));
      loadActivity();

      setTimeout(() => {
        const el = document.getElementById(sectionId || "signin-activity");
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    };

    window.addEventListener("dashboard:navigate-tab", handleNavigate);

    if (window.location.hash === "#signin-activity") {
      handleNavigate({ detail: { tab: 2, sectionId: "signin-activity" } });
    }

    return () => window.removeEventListener("dashboard:navigate-tab", handleNavigate);
  }, [loadActivity]);

  // ─── Manual Refetch (Does NOT reload page) ─────────────────────────────────
  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    await Promise.allSettled([
      loadOverview(range, controller.signal),
      loadSignins(range, controller.signal),
      sectionsVisibleRef.current.mfa ? loadMfa(range, controller.signal) : Promise.resolve(),
      sectionsVisibleRef.current.inventory ? loadInventory(controller.signal) : Promise.resolve(),
      sectionsVisibleRef.current.activity ? loadActivity(controller.signal) : Promise.resolve(),
    ]);

    setLastRefreshed(new Date());
    setIsRefreshing(false);
  };

  const isDegraded = Object.values(degradedStatus).some(Boolean);

  // ─── Permission Denied State ───────────────────────────────────────────────
  if (!hasPermission) {
    return (
      <div className="dl-root">
        <div className="dl-page page-content">
          <Container maxWidth="xl" sx={{ py: 3, px: { xs: 2, md: 3 } }}>
            <ReusableHeader title="Identity Overview" />
            <div
              className="dl-card"
              style={{
                padding: "60px 24px",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 340,
                marginTop: 24,
              }}
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background: `${T.accent3}14`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 16,
                }}
              >
                <SecurityOutlinedIcon sx={{ fontSize: 30, color: T.accent3 }} />
              </div>
              <Typography sx={{ fontFamily: T.fontHeading, fontSize: 20, fontWeight: 800, color: T.textPrimary, mb: 1 }}>
                Access Denied
              </Typography>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: 13, color: T.textMuted, maxWidth: 440 }}>
                You do not have permission to view the Identity Overview dashboard. Please contact your organization administrator to request access.
              </Typography>
            </div>
          </Container>
        </div>
      </div>
    );
  }

  // ─── Header Action Elements ────────────────────────────────────────────────
  const headerActions = (
    <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
      {isDegraded && (
        <Tooltip title="Some metrics were returned using cached approximations or degraded sub-queries">
          <Chip
            size="small"
            icon={<WarningAmberOutlinedIcon sx={{ fontSize: "14px !important" }} />}
            label="Partial Data"
            sx={{
              background: `${T.accent5}18`,
              color: T.accent5,
              border: `1px solid ${T.accent5}30`,
              fontWeight: 700,
              fontSize: 11,
              height: 24,
            }}
          />
        </Tooltip>
      )}

      {/* Range Selector (24h / 7d / 30d) */}
      <ToggleButtonGroup
        value={range}
        exclusive
        onChange={(e, val) => val && handleRangeChange(val)}
        size="small"
        aria-label="time range selector"
        sx={{
          height: 30,
          background: T.bgCard,
          border: `1px solid ${T.border}`,
          borderRadius: "8px",
          "& .MuiToggleButton-root": {
            fontFamily: T.fontHeading,
            fontSize: 11,
            fontWeight: 700,
            textTransform: "none",
            px: 1.5,
            py: 0.25,
            color: T.textSecondary,
            border: "none",
            "&.Mui-selected": {
              background: `${T.accent1}1A`,
              color: T.accent1,
            },
          },
        }}
      >
        <ToggleButton value="24h" aria-label="last 24 hours">24h</ToggleButton>
        <ToggleButton value="7d" aria-label="last 7 days">7d</ToggleButton>
        <ToggleButton value="30d" aria-label="last 30 days">30d</ToggleButton>
      </ToggleButtonGroup>

      {/* Updated hh:mm */}
      <Typography sx={{ fontFamily: T.fontBody, fontSize: 12, color: T.textMuted, whiteSpace: "nowrap" }}>
        Updated {lastRefreshed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
      </Typography>

      {/* Refresh Button */}
      <Tooltip title="Refresh metrics">
        <IconButton
          onClick={handleRefresh}
          disabled={isRefreshing}
          size="small"
          aria-label="refresh dashboard"
          sx={{
            border: `1px solid ${T.border}`,
            borderRadius: "8px",
            color: T.textSecondary,
            background: T.bgCard,
            "&:hover": {
              background: `${T.accent1}0D`,
              borderColor: T.accent1,
              color: T.accent1,
            },
          }}
        >
          <RefreshIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );

  return (
    <div className="dl-root">
      <div className="dl-page page-content">
        <div className="dl-inner">
          <Container maxWidth="xl" sx={{ py: 3, px: { xs: 2, md: 3 } }}>
            {/* Header: Title, Range selector, Updated hh:mm, Refresh */}
            <ReusableHeader title="Identity Overview" actionButton={headerActions} />

            {/* Error Banner for Overview if present */}
            {overviewError && (
              <Box
                mb={2}
                px={2}
                py={1}
                sx={{
                  background: `${T.accent3}12`,
                  border: `1px solid ${T.accent3}30`,
                  borderRadius: "8px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography sx={{ fontSize: 12, color: T.accent3, fontWeight: 600 }}>
                  {overviewError}
                </Typography>
                <Button
                  size="small"
                  onClick={() => loadOverview(range)}
                  sx={{ color: T.accent3, fontWeight: 700, fontSize: 11, textTransform: "none" }}
                >
                  Retry
                </Button>
              </Box>
            )}

            {/* Row 1: 6 KPI Cards */}
            <KpiCardsSection kpis={overviewData?.kpis} loading={overviewLoading} T={T} />

            {/* Row 2: Needs Attention (left) beside Sign-in Activity (right) */}
            <Grid container spacing={2} mb={2.5}>
              <Grid item xs={12}>
                <NeedsAttentionCard attention={overviewData?.attention || []} T={T} />
              </Grid>
              {/* Sign-in Activity chart temporarily commented out until telemetry event data is populated
              <Grid item xs={12} lg={8}>
                <IntersectionSection
                  minHeight={380}
                  onVisible={() => {
                    setSectionsVisible((prev) => ({ ...prev, signins: true }));
                    if (!signinsData && !signinsLoading) loadSignins(range);
                  }}
                >
                  {(visible) =>
                    visible ? (
                      <Suspense fallback={<SectionSkeleton height={380} />}>
                        <SignInActivitySection
                          data={signinsData}
                          dataSince={signinsData?.notes?.dataSince || mfaData?.notes?.dataSince || overviewData?.dataSince}
                          loading={signinsLoading}
                          error={signinsError}
                          onRetry={() => loadSignins(range)}
                          T={T}
                        />
                      </Suspense>
                    ) : (
                      <SectionSkeleton height={380} />
                    )
                  }
                </IntersectionSection>
              </Grid>
              */}
            </Grid>

            {/* Row 3: Top Applications, Sign-ins by Protocol, MFA Methods Used */}
            <IntersectionSection
              minHeight={330}
              onVisible={() => {
                setSectionsVisible((prev) => ({ ...prev, signins: true, mfa: true }));
                if (!signinsData && !signinsLoading) loadSignins(range);
                if (!mfaData && !mfaLoading) loadMfa(range);
              }}
            >
              {(visible) =>
                visible ? (
                  <Suspense fallback={<SectionSkeleton height={330} />}>
                    <ProtocolAndMethodsSection
                      topApplications={signinsData?.topApplications || []}
                      byProtocolTimeSeries={signinsData?.timeSeries?.byProtocol || []}
                      mfaMethodMix={mfaData?.methodMix || {}}
                      loading={signinsLoading || mfaLoading}
                      error={signinsError || mfaError}
                      onRetry={() => {
                        loadSignins(range);
                        loadMfa(range);
                      }}
                      T={T}
                    />
                  </Suspense>
                ) : (
                  <SectionSkeleton height={330} />
                )
              }
            </IntersectionSection>

            {/* Row 4: MFA Outcomes Over Time & Approval Time Latency */}
            <IntersectionSection
              minHeight={340}
              onVisible={() => {
                setSectionsVisible((prev) => ({ ...prev, mfa: true }));
                if (!mfaData && !mfaLoading) loadMfa(range);
              }}
            >
              {(visible) =>
                visible ? (
                  <Suspense fallback={<SectionSkeleton height={340} />}>
                    <MfaOutcomesSection
                      mfaData={mfaData}
                      loading={mfaLoading}
                      error={mfaError}
                      onRetry={() => loadMfa(range)}
                      T={T}
                    />
                  </Suspense>
                ) : (
                  <SectionSkeleton height={340} />
                )
              }
            </IntersectionSection>

            {/* Row 5: Applications & MFA Enforcement, Enrollment, Device Health, Posture */}
            <IntersectionSection
              minHeight={400}
              onVisible={() => {
                setSectionsVisible((prev) => ({ ...prev, inventory: true }));
                loadInventory();
              }}
            >
              {(visible) =>
                visible ? (
                  <Suspense fallback={<SectionSkeleton height={400} />}>
                    <InventoryFleetSection
                      inventory={inventoryData}
                      loading={inventoryLoading}
                      error={inventoryError}
                      onRetry={() => loadInventory()}
                      T={T}
                    />
                  </Suspense>
                ) : (
                  <SectionSkeleton height={400} />
                )
              }
            </IntersectionSection>

            {/* Row 6: Tabs "Admin activity" / "Security events" / "Top failed sign-ins" */}
            <div id="signin-activity" style={{ scrollMarginTop: "80px" }}>
              <IntersectionSection
                minHeight={380}
                onVisible={() => {
                  setSectionsVisible((prev) => ({ ...prev, activity: true }));
                  loadActivity();
                }}
              >
                {(visible) =>
                  visible ? (
                    <Suspense fallback={<SectionSkeleton height={380} />}>
                      <ActivityTabsSection
                        activity={activityData}
                        loading={activityLoading}
                        error={activityError}
                        onRetry={() => loadActivity()}
                        T={T}
                      />
                    </Suspense>
                  ) : (
                    <SectionSkeleton height={380} />
                  )
                }
              </IntersectionSection>
            </div>

            {/* Footer */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              pt={2}
              pb={2}
              sx={{ borderTop: `1px solid ${T.border}` }}
            >
              <Typography sx={{ fontFamily: T.fontBody, fontSize: 11, color: T.textMuted }}>
                Identity Overview · StarSecure IAM
              </Typography>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: 11, color: T.textMuted }}>
                {overviewData?.kpis?.devices?.usable ? `${overviewData.kpis.devices.usable} usable devices · ` : ""}
                {overviewData?.kpis?.activeUsers?.total ? `${overviewData.kpis.activeUsers.total} users` : ""}
              </Typography>
            </Box>
          </Container>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;